import React from "react";
import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Box,
  BlockStack,
  InlineStack,
  Text,
  Link,
  Button,
  ResourceList,
  ResourceItem,
  Thumbnail
} from "@shopify/polaris";
import { DiscountsMajor } from "@shopify/polaris-icons";
import { GET_TASK_STATUS } from "../../../graphql/Queries/TaskStatus";
import { GET_SHOP_INFO } from "../../../graphql/Queries/Shop";
import { CampaignStatusEnum as Status } from "../../CampaignsPage/components/CampaignStatusEnum";
import { DiscountType, SaleTypes } from "../../../features/DiscountPlanning/Discount";
import { getStatusBadge } from "../../../utils/SaleStatusUtils";
import { formatDateTime } from "../../../utils/Dates";

const FAILED = [Status.FailedActivating, Status.FailedDeactivating, Status.FailedTooManyProducts];
const RUNNING = [Status.Starting, Status.Started, Status.SyncingProducts, Status.Ending];
const UPCOMING = [Status.Scheduled, Status.PendingUpgrade];
const EDITABLE = [Status.Ended, Status.Scheduled, Status.PendingUpgrade, Status.Started];

// Dates before the campaign was requested stand for "none" (no-end-date sales arrive as 0001-01-01),
// so show the requested date instead, as the Campaigns page does
const effectiveDate = (value, requested) => {
  const date = new Date(value);
  return date < requested ? requested : date;
};

// Failed first (they need attention), then running, then scheduled (soonest first), then the latest ended
export const pickRecentCampaigns = (tasks, count) => {
  const withAction = tasks.filter(task => task.Action);
  const time = (task, field) => effectiveDate(task[field], new Date(task.Requested)).getTime();
  return [
    ...withAction.filter(task => FAILED.includes(task.Status)),
    ...withAction.filter(task => RUNNING.includes(task.Status)),
    ...withAction.filter(task => UPCOMING.includes(task.Status)).sort((a, b) => time(a, "StartDate") - time(b, "StartDate")),
    ...withAction.filter(task => task.Status === Status.Ended).sort((a, b) => time(b, "EndDate") - time(a, "EndDate")),
  ].slice(0, count);
};

// Same wording as the schedule badges on the Campaigns page
export const scheduleText = (task, locale) => {
  const requested = new Date(task.Requested);
  const start = formatDateTime(effectiveDate(task.StartDate, requested), locale);
  const end = formatDateTime(effectiveDate(task.EndDate, requested), locale);
  switch (task.Status) {
    case Status.Starting:
    case Status.Started:
    case Status.SyncingProducts:
    case Status.Ending:
      return new Date(task.EndDate) < requested ? "No end date scheduled" : `Ends ${end}`;
    case Status.Ended:
      return `Ended ${end}`;
    case Status.FailedActivating:
    case Status.FailedDeactivating:
      return "Discount partially applied";
    case Status.PendingUpgrade:
      return `Planned start ${start}`;
    default:
      return `Starts ${start}`;
  }
};

// Untitled campaigns go by their discount, as on the Campaigns page
export const campaignName = (task, currencyCode) => {
  if (task.Title) return task.Title;
  if (task.SaleType === SaleTypes.DifferentDiscounts) return "Different discounts";
  const { Action: type, Value: value } = task.Action;
  if (type === DiscountType.SetConstant) return `${value} ${currencyCode} fixed price`;
  return `${value * -1}${type === DiscountType.AdjustByConstant ? ` ${currencyCode}` : "%"} discount`;
};

/**
 * The few campaigns worth a glance from the dashboard, or a nudge to create the first one.
 * Takes the setup guide's place once the merchant dismisses it.
 */
const RecentCampaigns = ({ locale, count = 3, onCreateSale = () => {} }) => {
  const dateLocale = locale || "en-US"; // App passes the URL's locale param, which can be null
  const navigate = useNavigate();
  const { data } = useQuery(GET_TASK_STATUS(), { fetchPolicy: "cache-and-network" });
  const { data: shopData } = useQuery(GET_SHOP_INFO());
  const currencyCode = shopData?.shop?.currencyCode || "USD";
  const campaigns = pickRecentCampaigns(data?.tasks || [], count);

  // Not Polaris EmptyState: this SVG has only a viewBox, so EmptyState sizes it 0x0
  if (data && campaigns.length === 0) {
    return (
      <Card>
        <Box paddingBlock="600">
          <BlockStack gap="400" inlineAlign="center">
            <img src="https://cdn.shopify.com/s/files/1/0757/9955/files/empty-state.svg" alt="" width={226} />
            <Text as="h2" variant="headingMd">Create a sales campaign</Text>
            <Text as="p" tone="subdued" alignment="center">
              Also, create urgency and awareness with announcement bars and countdown timers.
            </Text>
            <Button variant="primary" onClick={onCreateSale}>Create sale</Button>
          </BlockStack>
        </Box>
      </Card>
    );
  }

  return (
    <Card padding="0">
      <Box padding="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingSm">Recent campaigns</Text>
          <Link url="/campaigns">View all</Link>
        </InlineStack>
      </Box>
      <ResourceList
        resourceName={{ singular: "campaign", plural: "campaigns" }}
        items={campaigns}
        loading={!data}
        renderItem={(task) => {
          const name = campaignName(task, currencyCode);
          return (
            <ResourceItem
              id={task.Id}
              url={EDITABLE.includes(task.Status) ? `/campaigns/edit/${task.Id}` : undefined}
              accessibilityLabel={`Edit ${name}`}
              media={<Thumbnail size="small" alt="" source={task.ThumbnailSource || DiscountsMajor} />}
              verticalAlignment="center"
            >
              <InlineStack align="space-between" blockAlign="center" wrap={false} gap="300">
                <BlockStack gap="050">
                  <Text as="span" fontWeight="semibold">{name}</Text>
                  <Text as="span" variant="bodySm" tone="subdued">{scheduleText(task, dateLocale)}</Text>
                </BlockStack>
                {getStatusBadge(task.Status, () => navigate("/subscription"), task.DoneCount, task.SelectionCount, new Date(task.EndDate))}
              </InlineStack>
            </ResourceItem>
          );
        }}
      />
    </Card>
  );
};

export default RecentCampaigns;
